import { useState } from 'react';
import { useApiData } from 'lib/api';
import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { ProfileSummaryTimelineItem, ProfileSummaryTimelineToggle } from '.';

import type { GetUserUpcomingItemsRes } from 'shared-types/responses';

interface Props {
  isUserPage: boolean;
  targetUserId: number;
}

const ProfileSummaryUpcoming = ({ isUserPage, targetUserId }: Props) => {
  const { messages, theme, user: loggedInUser } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const [showAll, setShowAll] = useState(false);

  const { data, loading } = useApiData<GetUserUpcomingItemsRes>({
    url: `/admin/users/${targetUserId}/upcoming-items`
  });

  const NUMBER_OF_ITEMS_LIMIT = 5;

  const totalNumberOfItems = data?.upcomingItems?.length || 0;

  const upcomingItems = showAll
    ? data?.upcomingItems
    : data?.upcomingItems?.slice(0, NUMBER_OF_ITEMS_LIMIT);

  const itemIcons = {
    'calendar-event': ['far', 'calendar-days'],
    course: 'book',
    deadline: ['far', 'clock'],
    'individual-achievement': 'star',
    path: 'tasks',
    ...Object.keys(achievementTypes).reduce(
      (achievementIcons, achievementType) => {
        achievementIcons[achievementType] =
          achievementTypes[achievementType].icon;
        return achievementIcons;
      },
      {} as Record<string, string>
    )
  };

  const itemTypes = {
    'calendar-event': messages.eventCalendarEvent,
    course: messages.commonCourse,
    deadline: {
      course: messages.profileTimelineCourseDeadline,
      path: messages.profileTimelinePathDeadline
    },
    'expiring-completion': {
      achievement: messages.completionsExpiring,
      course: messages.profileTimelineCourseExpiring
    },
    'individual-achievement': messages.individualAchievement,
    path: messages.path,
    ...Object.keys(achievementTypes).reduce(
      (achievementIcons, achievementType) => {
        achievementIcons[achievementType] =
          achievementTypes[achievementType].title;
        return achievementIcons;
      },
      {} as Record<string, string>
    )
  };

  const profileBasePath = isUserPage ? `/users/${targetUserId}` : '/profile';

  return (
    <div className="upcoming">
      <Box
        loading={loading}
        padding={0}
        style={{ height: '100%' }}
        title={messages.profileTimelineUpcoming}
        titleTooltip={
          isUserPage && !loggedInUser?.roles?.includes('superadmin')
            ? messages.profileTimelineInfo
            : undefined
        }
      >
        <div className="content">
          {upcomingItems?.length ? (
            <div className="items" style={{ marginTop: 20 }}>
              {upcomingItems.map((upcomingItem, i) => (
                <ProfileSummaryTimelineItem
                  key={i}
                  category={
                    upcomingItem.itemType === 'calendar-event'
                      ? 'default'
                      : 'warning-light'
                  }
                  date={
                    (upcomingItem.itemType === 'calendar-event'
                      ? upcomingItem.itemData.startDate
                      : upcomingItem.itemType === 'deadline'
                      ? upcomingItem.itemData.deadline
                      : upcomingItem.itemType === 'expiring-completion'
                      ? upcomingItem.itemData.expirationDate
                      : upcomingItem.itemType === 'upcoming-completion'
                      ? upcomingItem.itemData.completionDate
                      : null) as string | null
                  }
                  icon={
                    upcomingItem.itemType === 'expiring-completion' ||
                    upcomingItem.itemType === 'upcoming-completion'
                      ? upcomingItem.itemData.type === 'achievement'
                        ? itemIcons[upcomingItem.itemData.achievement.type]
                        : itemIcons[upcomingItem.itemData.type]
                      : itemIcons[upcomingItem.itemType]
                  }
                  title={
                    upcomingItem.itemType === 'calendar-event'
                      ? upcomingItem.itemData.title
                      : upcomingItem.itemData.type === 'achievement'
                      ? upcomingItem.itemData.achievement?.title
                      : upcomingItem.itemData.type === 'course'
                      ? upcomingItem.itemData.course?.title
                      : upcomingItem.itemData.type === 'individual-achievement'
                      ? upcomingItem.itemData.title
                      : upcomingItem.itemData.type === 'path'
                      ? upcomingItem.itemData.path?.title
                      : ''
                  }
                  titleHref={
                    upcomingItem.itemType === 'calendar-event'
                      ? `/calendar/${upcomingItem.itemData.id}`
                      : upcomingItem.itemData.type === 'achievement'
                      ? `${profileBasePath}/achievements`
                      : upcomingItem.itemData.type === 'course'
                      ? `${profileBasePath}/courses/${upcomingItem.itemData.course?.id}`
                      : upcomingItem.itemData.type === 'path'
                      ? `${profileBasePath}/paths/${upcomingItem.itemData.path?.id}`
                      : ''
                  }
                  type={
                    upcomingItem.itemType === 'deadline'
                      ? itemTypes[upcomingItem.itemType][
                          upcomingItem.itemData.type
                        ]
                      : upcomingItem.itemType === 'expiring-completion'
                      ? itemTypes[upcomingItem.itemType][
                          upcomingItem.itemData.type
                        ]
                      : upcomingItem.itemType === 'upcoming-completion'
                      ? upcomingItem.itemData.type === 'achievement'
                        ? itemTypes[upcomingItem.itemData.achievement.type]
                        : itemTypes[upcomingItem.itemData.type]
                      : itemTypes[upcomingItem.itemType]
                  }
                />
              ))}

              {totalNumberOfItems > NUMBER_OF_ITEMS_LIMIT ? (
                <ProfileSummaryTimelineToggle
                  isExpanded={showAll}
                  numberOfAdditionalItems={
                    totalNumberOfItems - NUMBER_OF_ITEMS_LIMIT
                  }
                  onClick={() => setShowAll(!showAll)}
                />
              ) : null}
            </div>
          ) : null}

          {!upcomingItems?.length ? (
            <EmptyIndicator
              icon={['far', 'calendar-alt']}
              title={messages.profileTimelineUpcomingEmpty}
            />
          ) : null}
        </div>
      </Box>

      <style jsx>{`
        .upcoming {
          height: 100%;
        }
        .content {
          padding: 10px 15px 40px;
        }
        .items {
          margin-top: 50px;
        }
        .year {
          margin: 0px 0 20px;
          text-align: left;
        }
        .year-item {
          color: ${theme.textColor};
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1;
        }
        .actions {
          font-size: 1.6rem;
          margin-top: 10px;
          padding: 15px;
          text-align: center;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .content {
            padding: 10px 20px 40px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileSummaryUpcoming;
